import { useCallback, useContext } from 'react';
import type { FileUploadFile } from '@trinity/chat';
import { UploadFilesBaseContext } from '@trinity/chat-components/containers/WorkspaceFileUpload/Context.js';
import {
  useFileUploadEffects,
  useUploadFileToS3,
} from '@trinity/chat-components/containers/WorkspaceFileUpload/hooks/index.js';
import { type UploadFileParams, type UploadFilesParams } from '@trinity/chat-components/containers/WorkspaceFileUpload/interfaces.base.js';
import { useThrottleCallback } from '@trinity/chat-components/hooks/useThrottleCallback';
import { computeChecksum } from '@trinity/chat-components/utils';
import { ChatLoggerContext } from '../Chat';

type Props = {
  children: React.ReactNode;
  onInvalidFile?: (file: FileUploadFile) => void;
};

const MaxConcurrentFileUploads = 3;

export const UploadFilesContainer = ({
  onInvalidFile,
  ...props
}: Props) => {

  const effects = useFileUploadEffects();

  const uploadFileToS3 = useUploadFileToS3();
  const uploadFileToS3Throttled = useThrottleCallback(uploadFileToS3, MaxConcurrentFileUploads);
  const loggerCtx = useContext(ChatLoggerContext);

  const uploadFile = useCallback(async (data: UploadFileParams) => {
    const checksum = await computeChecksum(data.file);

    const file = {
      checksum,
      identifier: data.identifier,
      lastModified: data.file.lastModified,
      name: data.file.name,
      size: data.file.size,
      type: data.file.type,
    };

    try {
      const { fileUpload } = await effects.queue({
        file,
        options: data.options,
        parentObjectId: data.parentObjectId,
        uploadIdentifier: data.uploadIdentifier,
        workspaceId: data.workspaceId,
        respondentCountry: data.respondentCountry,
      });

      return uploadFileToS3Throttled(data.file, fileUpload);
    } catch (e) {
      console.log(e);
      loggerCtx.captureException(e);
      onInvalidFile?.(file);
    }
  }, [
    loggerCtx,
    effects,
    onInvalidFile,
    uploadFileToS3Throttled,
  ]);

  const uploadFiles = useCallback((data: UploadFilesParams) => {
    for (const identifier of Object.keys(data.files)) {
      const file = data.files[identifier];
      uploadFile({
        file,
        identifier,
        options: {
          convertToTranscript: data.isTranscriptImport,
          replaceExisting: data.replaceExisting,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          tags: data.tags,
          transcribe: data.transcribeable.includes(identifier),
        },
        parentObjectId: data.parentObjectId,
        workspaceId: data.workspaceId,
        uploadIdentifier: data.uploadIdentifier,
        respondentCountry: data.respondentCountry,
      });
    }
  }, [
    uploadFile,
  ]);

  return (
    <UploadFilesBaseContext.Provider value={uploadFiles}>
      {props.children}
    </UploadFilesBaseContext.Provider>
  );
};